import { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  User,
  CalendarBlank,
  Stethoscope,
  NotePencil,
  ChatCircleText,
  PencilSimple,
  CaretDown,
  CaretUp,
  Warning,
  CalendarX,
  ArrowsClockwise,
  VideoCamera,
  LinkSimple,
} from "@phosphor-icons/react";
import DatePicker from "../../../ui/DatePicker";
import TimePicker from "../../../ui/TimePicker";
import { HEBREW_DAYS_FULL, formatHebrewDate } from "../../../../utils/dateUtils";
import { getFrequencyLabel } from "../../../../utils/appointmentSmsPreview";
import { WIZARD_STEPS } from "../useAppointmentForm";
import SeriesOverviewPanel from "../edit/SeriesOverviewPanel";
import CommunicationPanel from "../edit/CommunicationPanel";

/**
 * Step 4 — summary: minimal detail sections (tap → jump to step), notes,
 * SMS toggles + expandable per-message preview.
 */
function SummaryStep({ form }) {
  const {
    formData,
    updateField,
    goToStep,
    treatmentTypes,
    accessibleRooms,
    canAccessFinance,
    isEndMeetingSmsEnabled,
    isPastDateTime,
    usePunchCard,
    smsPreviewItems,
    toggleSmsPreviewItem,
    setAllSmsEnabled,
    updateSmsPreviewItem,
    getPatientPhone,
    smsError,
    stepErrors,
    isEditing,
    liveAppointment,
    handleRestoreAppointment,
    isRestoring,
    unassignedTherapistForPatient,
    seriesUnassignedIdx = [],
  } = form;

  const [showSmsDetails, setShowSmsDetails] = useState(false);
  const [meetLinkCopied, setMeetLinkCopied] = useState(false);
  const navigate = useNavigate();

  const tt = treatmentTypes.find((t) => t.id === formData.treatmentTypeId);
  const room = accessibleRooms.find((r) => r.id === formData.roomId);
  // Edit mode renders the summary before the hydrate effect commits — the
  // date can be empty for one frame, and date-fns throws on invalid dates.
  const summaryDate = formData.date ? new Date(`${formData.date}T00:00:00`) : null;
  const hasValidDate = summaryDate && !Number.isNaN(summaryDate.getTime());
  const dayName = hasValidDate ? HEBREW_DAYS_FULL[summaryDate.getDay()] : "";
  const dateLabel = hasValidDate ? formatHebrewDate(summaryDate) : "";
  // Edit mode: the series size comes from the appointment doc, not the
  // create-flow form default.
  const seriesCount = isEditing
    ? form.appointment?.recurringTotalCount || "—"
    : formData.seriesTotalCount;
  const patientPhone = getPatientPhone();
  const anySmsEnabled = smsPreviewItems.some((i) => i.enabled);
  const enabledCount = smsPreviewItems.filter((i) => i.enabled).length;

  const detailsParts = [];
  if (tt) detailsParts.push(tt.name);
  if (canAccessFinance && formData.paymentAmount !== "") {
    detailsParts.push(
      usePunchCard && !formData.isRecurring
        ? "מנוקב מכרטיסיה"
        : `₪${parseFloat(formData.paymentAmount || 0).toLocaleString("he-IL")}`
    );
  }
  if (room) detailsParts.push(room.name);
  if (formData.isOnlineMeeting) detailsParts.push("פגישת וידאו אונליין");
  // E6: surface the assignment gate here too — submit bounces back to the
  // Details step until it is resolved.
  if (unassignedTherapistForPatient) {
    detailsParts.push(
      `המטופל/ת אינו/ה משויך/ת ל${unassignedTherapistForPatient.name}`
    );
  } else if (seriesUnassignedIdx.length > 0) {
    detailsParts.push("המטפל/ת אינו/ה משויך/ת למטופל/ת בחלק מהמפגשים");
  }

  const sections = [
    {
      step: WIZARD_STEPS.PATIENT,
      icon: <User size={15} weight="fill" />,
      label: "מטופל/ת",
      value: formData.patientName + (formData.isTempCustomer ? " (לקוח מזדמן)" : ""),
    },
    {
      step: WIZARD_STEPS.SCHEDULE,
      icon: <CalendarBlank size={15} weight="fill" />,
      label: "מועד",
      value: !hasValidDate
        ? "—"
        : formData.isRecurring
        ? `יום ${dayName}, ${dateLabel} בשעה ${formData.startTime} · ${getFrequencyLabel(formData.recurringFrequency)}, ${seriesCount} מפגשים`
        : `יום ${dayName}, ${dateLabel} בשעה ${formData.startTime} · ${formData.duration} דקות`,
    },
    {
      step: WIZARD_STEPS.DETAILS,
      icon: <Stethoscope size={15} weight="fill" />,
      label: "פרטים ותשלום",
      value: detailsParts.length > 0 ? detailsParts.join(" · ") : "ללא פרטים נוספים",
    },
  ];

  const isCancelled = isEditing && liveAppointment?.status === "cancelled";

  return (
    <div className="apt-wiz-step-panel" key="step-summary">
      {isCancelled && (
        <div className="apt-wiz-cancelled-banner">
          <div className="apt-wiz-gate-text">
            <CalendarX size={16} weight="fill" />
            <span>
              <strong>הפגישה בוטלה</strong>
              {liveAppointment.cancelledBy === "patient"
                ? " — המטופל ביטל את ההגעה דרך טופס האישור."
                : liveAppointment.cancelledBy === "therapist"
                ? " — הפגישה סומנה כבוטלה על ידי המטפל."
                : ""}
              {liveAppointment.cancellationFeeRecorded ? " נרשמו דמי ביטול." : ""}
              {liveAppointment.cancellationReason
                ? ` סיבה: ${liveAppointment.cancellationReason}`
                : ""}
            </span>
          </div>
          <button
            type="button"
            className="apt-wiz-gate-btn"
            onClick={handleRestoreAppointment}
            disabled={isRestoring}
          >
            <ArrowsClockwise size={13} weight="bold" />{" "}
            {isRestoring
              ? "משחזר..."
              : liveAppointment.isRecurring
              ? "שחזר פגישה לסדרה"
              : "שחזר פגישה"}
          </button>
        </div>
      )}

      <div className="apt-wiz-summary-sections">
        {sections.map((section) => {
          // Patient never changes on an existing appointment.
          const locked = isEditing && section.step === WIZARD_STEPS.PATIENT;
          return (
            <button
              key={section.step}
              type="button"
              className={`apt-wiz-summary-section ${locked ? "locked" : ""}`}
              onClick={() =>
                !locked && goToStep(section.step, { fromSummary: true })
              }
              disabled={locked}
            >
              <span className="apt-wiz-summary-icon">{section.icon}</span>
              <span className="apt-wiz-summary-content">
                <span className="apt-wiz-summary-label">{section.label}</span>
                <span className="apt-wiz-summary-value">{section.value}</span>
              </span>
              {!locked && (
                <PencilSimple size={13} className="apt-wiz-summary-edit" />
              )}
            </button>
          );
        })}
      </div>

      {isEditing && <SeriesOverviewPanel form={form} />}
      {isEditing && <CommunicationPanel form={form} />}

      {/* Online video meeting — join + copy patient link (edit mode only) */}
      {isEditing &&
        liveAppointment?.isOnlineMeeting === true &&
        !isCancelled && (
          <div className="apt-wiz-video-panel">
            <div className="apt-wiz-video-info">
              <VideoCamera size={15} weight="fill" />
              <span>פגישת וידאו אונליין</span>
            </div>
            <div className="apt-wiz-video-actions">
              <button
                type="button"
                className="apt-wiz-video-join-btn"
                onClick={() =>
                  navigate(`/dashboard/video-meeting/${liveAppointment.id}`)
                }
              >
                <VideoCamera size={14} weight="fill" /> הצטרפות לשיחה
              </button>
              {liveAppointment.videoMeetLinkId && (
                <button
                  type="button"
                  className="apt-wiz-video-copy-btn"
                  onClick={() => {
                    navigator.clipboard
                      ?.writeText(
                        `https://clinikit.co.il/meet/${liveAppointment.videoMeetLinkId}`
                      )
                      .then(() => {
                        setMeetLinkCopied(true);
                        setTimeout(() => setMeetLinkCopied(false), 2000);
                      })
                      .catch(() => {});
                  }}
                >
                  <LinkSimple size={14} />{" "}
                  {meetLinkCopied ? "הקישור הועתק" : "העתקת קישור למטופל"}
                </button>
              )}
            </div>
          </div>
        )}

      {/* SMS — create flow only; edit SMS actions arrive with Phase 2c */}
      {isEditing ? null : isPastDateTime ? (
        <div className="apt-wiz-hint-card">
          <ChatCircleText size={14} weight="fill" />
          <span>פגישה במועד שעבר — לא יישלחו הודעות SMS</span>
        </div>
      ) : !patientPhone ? (
        <div className="apt-wiz-hint-card">
          <ChatCircleText size={14} weight="fill" />
          <span>לא נמצא מספר נייד — לא יישלחו הודעות SMS</span>
        </div>
      ) : smsPreviewItems.length > 0 ? (
        <div className="apt-wiz-sms-block">
          <label className={`apt-wiz-toggle-card ${anySmsEnabled ? "on" : ""}`}>
            <input
              type="checkbox"
              checked={anySmsEnabled}
              onChange={(e) => setAllSmsEnabled(e.target.checked)}
            />
            <span className="apt-wiz-toggle-check" />
            <span className="apt-wiz-toggle-text">
              <span className="apt-wiz-toggle-title">
                <ChatCircleText size={14} weight="fill" /> הודעות SMS למטופל
              </span>
              <span className="apt-wiz-toggle-sub">
                {anySmsEnabled
                  ? `${enabledCount} הודעות יישלחו (אישור הגעה ותזכורות)`
                  : "לא יישלחו הודעות לפגישה זו"}
              </span>
            </span>
          </label>

          {anySmsEnabled || smsPreviewItems.some((i) => i.suppressedReason) ? (
            <button
              type="button"
              className="apt-wiz-sms-details-toggle"
              onClick={() => setShowSmsDetails((v) => !v)}
            >
              {showSmsDetails ? <CaretUp size={13} /> : <CaretDown size={13} />}
              פירוט ההודעות ומועדי שליחה
            </button>
          ) : null}

          {showSmsDetails && (
            <div className="apt-wiz-sms-items">
              {smsPreviewItems.map((item) => (
                <div
                  key={item.id}
                  className={`apt-wiz-sms-item ${item.enabled ? "" : "disabled"}`}
                >
                  <label className="apt-wiz-sms-item-head">
                    <input
                      type="checkbox"
                      checked={item.enabled}
                      onChange={() => toggleSmsPreviewItem(item.id)}
                    />
                    <span className="apt-wiz-sms-item-label">{item.label}</span>
                    <span className="apt-wiz-sms-item-when">
                      {item.isImmediate
                        ? "נשלחת מיד"
                        : `${item.scheduledDate?.split("-").reverse().slice(0, 2).join("/")} · ${item.scheduledTime}`}
                    </span>
                  </label>
                  {item.suppressedReason && (
                    <div className="apt-wiz-sms-suppressed">
                      <Warning size={12} weight="fill" /> {item.suppressedReason}
                    </div>
                  )}
                  {(item.enabled || item.suppressedReason) && !item.isImmediate && (
                    <div className="apt-wiz-sms-item-schedule">
                      <DatePicker
                        name={`sms-date-${item.id}`}
                        value={item.scheduledDate}
                        onChange={(e) =>
                          updateSmsPreviewItem(item.id, "date", e.target.value)
                        }
                      />
                      <TimePicker
                        name={`sms-time-${item.id}`}
                        value={item.scheduledTime}
                        onChange={(e) =>
                          updateSmsPreviewItem(item.id, "time", e.target.value)
                        }
                      />
                    </div>
                  )}
                  {item.enabled && (
                    <div className="apt-wiz-sms-message">{item.message}</div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      ) : null}

      {/* Video-link SMS fallback — the confirmation SMS carries the join
          link; when it's off, offer a dedicated SMS with just the link */}
      {!isEditing &&
        formData.isOnlineMeeting === true &&
        !isPastDateTime &&
        patientPhone &&
        !smsPreviewItems.some((i) => i.type === "initial" && i.enabled) && (
          <label
            className={`apt-wiz-toggle-card ${formData.sendVideoLinkSms ? "on" : ""}`}
          >
            <input
              type="checkbox"
              checked={formData.sendVideoLinkSms === true}
              onChange={(e) => updateField("sendVideoLinkSms", e.target.checked)}
            />
            <span className="apt-wiz-toggle-check" />
            <span className="apt-wiz-toggle-text">
              <span className="apt-wiz-toggle-title">
                <VideoCamera size={14} weight="fill" /> שליחת קישור השיחה ב-SMS
              </span>
              <span className="apt-wiz-toggle-sub">
                הודעת אישור ההגעה כבויה — ללא הודעה זו המטופל לא יקבל את
                הקישור לשיחת הווידאו
              </span>
            </span>
          </label>
        )}

      {/* End-meeting SMS — create only (edit keeps the appointment's setting) */}
      {!isEditing && isEndMeetingSmsEnabled && !isPastDateTime && patientPhone && (
        <label
          className={`apt-wiz-toggle-card ${formData.sendEndMeetingSms ? "on" : ""}`}
        >
          <input
            type="checkbox"
            checked={formData.sendEndMeetingSms}
            onChange={(e) => updateField("sendEndMeetingSms", e.target.checked)}
          />
          <span className="apt-wiz-toggle-check" />
          <span className="apt-wiz-toggle-text">
            <span className="apt-wiz-toggle-title">הודעת סיום פגישה</span>
            <span className="apt-wiz-toggle-sub">
              נשלחת למטופל אוטומטית בסיום הפגישה
            </span>
          </span>
        </label>
      )}

      <div className="apt-wiz-form-group">
        <label>
          <NotePencil size={13} weight="fill" /> הערות (פנימי)
        </label>
        <textarea
          rows={2}
          value={formData.notes}
          onChange={(e) => updateField("notes", e.target.value)}
          placeholder="הערות לפגישה — לא נשלחות למטופל"
        />
      </div>

      {smsError && <div className="apt-wiz-field-error">{smsError}</div>}
      {Object.keys(stepErrors).length > 0 && (
        <div className="apt-wiz-field-error">
          {Object.values(stepErrors)[0]}
        </div>
      )}
    </div>
  );
}

export default SummaryStep;
